import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Building,
  GitBranch,
  Users,
  Settings,
  Ruler,
  Hash,
  Percent,
  FolderTree,
  Calculator,
  ListTree,
  Warehouse,
  Package,
  ShoppingCart,
  Tags,
  Receipt,
  FileText,
  FileClock,
  DollarSign,
  ShoppingBag,
  Undo2,
  WifiOff,
  Banknote,
  Shield,
  Truck,
  MapPin,
  AlertTriangle,
  TrendingUp,
  ClipboardList,
  BarChart3,
  CreditCard,
  Bell,
  UserMinus,
  Target,
  ChevronRight
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/app/store/authStore'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar
} from '@/components/ui/sidebar'
import { NavUser } from './NavUser'

interface NavItem {
  to: string
  icon: React.ElementType
  label: string
  end?: boolean // Exact matching (prevents /inventory from matching /inventory/bulk-reconcile)
}

interface NavSubGroup {
  title: string
  icon: React.ElementType
  items: NavItem[]
}

interface NavGroup {
  title: string
  icon: React.ElementType
  items: NavItem[]
  subGroups?: NavSubGroup[]
}

// TEMPORARY: the Notifications page is restricted to a few named users while the
// Telegram channel is down. Keep this list in sync with the page's allowlist.
const NOTIFICATIONS_ALLOWED_USERNAMES = ['superadmin', 'acc_superadmin', 'manikandan']

/**
 * Ctrl/Cmd + click opens the destination in a new browser tab instead of
 * navigating the current one.
 */
const handleNavClick = (
  e: React.MouseEvent,
  item: NavItem,
  onNavigate?: () => void
): void => {
  if (e.ctrlKey || e.metaKey) {
    e.preventDefault()
    window.open(item.to, '_blank')
    return
  }
  onNavigate?.()
}

/** Mirrors NavLink's matching: `end` is exact, otherwise prefix-by-segment. */
const useIsItemActive = () => {
  const { pathname } = useLocation()
  return (item: NavItem): boolean =>
    item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`)
}

const NavLeafLink: React.FC<{ item: NavItem; onNavigate?: () => void }> = ({
  item,
  onNavigate
}) => {
  const isItemActive = useIsItemActive()
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isItemActive(item)} tooltip={item.label}>
        <Link to={item.to} onClick={(e) => handleNavClick(e, item, onNavigate)}>
          <item.icon />
          <span>{item.label}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

const NavSubLink: React.FC<{
  item: NavItem
  onNavigate?: () => void
  size?: 'sm' | 'md'
}> = ({ item, onNavigate, size = 'md' }) => {
  const isItemActive = useIsItemActive()
  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton asChild size={size} isActive={isItemActive(item)}>
        <Link to={item.to} onClick={(e) => handleNavClick(e, item, onNavigate)}>
          <item.icon />
          <span>{item.label}</span>
        </Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  )
}

/** A second-level collapsible (used only by Admin's sub-groups). */
const NavSubGroupCollapsible: React.FC<{ group: NavSubGroup; onNavigate?: () => void }> = ({
  group,
  onNavigate
}) => {
  const isItemActive = useIsItemActive()
  const hasActiveChild = group.items.some(isItemActive)
  const [open, setOpen] = useState(hasActiveChild)

  // Keep the branch containing the current route open as the user navigates.
  useEffect(() => {
    if (hasActiveChild) setOpen(true)
  }, [hasActiveChild])

  return (
    <Collapsible asChild open={open} onOpenChange={setOpen} className="group/subgroup">
      <SidebarMenuSubItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuSubButton asChild>
            <button type="button" className="w-full">
              <group.icon />
              <span className="flex-1 text-left">{group.title}</span>
              <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/subgroup:rotate-90" />
            </button>
          </SidebarMenuSubButton>
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
          <SidebarMenuSub className="mx-1.5 px-1.5">
            {group.items.map((item) => (
              <NavSubLink key={item.to} item={item} onNavigate={onNavigate} size="sm" />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuSubItem>
    </Collapsible>
  )
}

/**
 * Top-level nav group. Expanded it is a collapsible tree; collapsed to the icon
 * rail it flattens into a dropdown so every destination stays reachable.
 */
const NavGroupItem: React.FC<{ group: NavGroup; onNavigate?: () => void }> = ({
  group,
  onNavigate
}) => {
  const { state, isMobile } = useSidebar()
  const isItemActive = useIsItemActive()
  const isIconRail = state === 'collapsed' && !isMobile

  const allItems = [...group.items, ...(group.subGroups?.flatMap((g) => g.items) ?? [])]
  const hasActiveChild = allItems.some(isItemActive)
  const [open, setOpen] = useState(hasActiveChild)

  useEffect(() => {
    if (hasActiveChild) setOpen(true)
  }, [hasActiveChild])

  if (isIconRail) {
    return (
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton isActive={hasActiveChild} tooltip={group.title}>
              <group.icon />
              <span>{group.title}</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" sideOffset={8} className="w-60">
            <DropdownMenuLabel>{group.title}</DropdownMenuLabel>
            {group.items.map((item) => (
              <DropdownMenuItem key={item.to} asChild className="cursor-pointer">
                <Link to={item.to} onClick={(e) => handleNavClick(e, item, onNavigate)}>
                  <item.icon className="mr-2 h-4 w-4" />
                  <span>{item.label}</span>
                </Link>
              </DropdownMenuItem>
            ))}
            {group.subGroups?.map((subGroup) => (
              <React.Fragment key={subGroup.title}>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
                  {subGroup.title}
                </DropdownMenuLabel>
                {subGroup.items.map((item) => (
                  <DropdownMenuItem key={item.to} asChild className="cursor-pointer">
                    <Link to={item.to} onClick={(e) => handleNavClick(e, item, onNavigate)}>
                      <item.icon className="mr-2 h-4 w-4" />
                      <span>{item.label}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </React.Fragment>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    )
  }

  return (
    <Collapsible asChild open={open} onOpenChange={setOpen} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={group.title}>
            <group.icon />
            <span>{group.title}</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
          <SidebarMenuSub>
            {group.items.map((item) => (
              <NavSubLink key={item.to} item={item} onNavigate={onNavigate} />
            ))}
            {group.subGroups?.map((subGroup) => (
              <NavSubGroupCollapsible
                key={subGroup.title}
                group={subGroup}
                onNavigate={onNavigate}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

const AppSidebar: React.FC<React.ComponentProps<typeof Sidebar>> = ({ className, ...props }) => {
  const currentUsername = useAuthStore((state) => state.user?.username)
  const canSeeNotifications =
    !!currentUsername && NOTIFICATIONS_ALLOWED_USERNAMES.includes(currentUsername)
  const { isMobile, setOpenMobile } = useSidebar()

  // Close the mobile sheet after a navigation.
  const onNavigate = () => {
    if (isMobile) setOpenMobile(false)
  }

  const navItems: NavItem[] = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/leads', icon: Target, label: 'Leads' },
    { to: '/party', icon: Users, label: 'Parties' }
  ]

  const adminTopNavItems: NavItem[] = [
    { to: '/organizations', icon: Building, label: 'Organizations' },
    { to: '/sites', icon: GitBranch, label: 'Sites' }
  ]

  const itemsNavItems: NavItem[] = [
    { to: '/items', icon: Package, label: 'Items' },
    { to: '/items/collections', icon: FolderTree, label: 'Item Collections' },
    { to: '/items/internal-transfers', icon: Truck, label: 'Internal Transfers' },
    { to: '/items/low-stock-report', icon: AlertTriangle, label: 'Low Stock Report' }
  ]

  const quotesNavItems: NavItem[] = [
    { to: '/sales-orders/list', icon: ShoppingCart, label: 'Sales Orders' }
  ]

  const salesNavItems: NavItem[] = [
    { to: '/sales-invoices/list', icon: FileText, label: 'Sales Invoices' },
    { to: '/payments-in/list', icon: Receipt, label: 'Payments In' },
    { to: '/cheques/list', icon: Banknote, label: 'Cheque Register' },
    { to: '/credit-notes/list', icon: Undo2, label: 'Credit Notes' },
    { to: '/offline-invoices/list', icon: WifiOff, label: 'Offline Invoices' }
  ]

  const purchasesNavItems: NavItem[] = [
    { to: '/purchase-orders/list', icon: ClipboardList, label: 'Purchase Orders' },
    { to: '/purchase-invoices/list', icon: Receipt, label: 'Purchase Invoices' },
    { to: '/debit-notes/list', icon: Undo2, label: 'Debit Notes' },
    { to: '/expenses/list', icon: DollarSign, label: 'Expenses' },
    { to: '/payments-out/list', icon: Receipt, label: 'Payments Out' }
  ]

  const transactionsNavItems: NavItem[] = [
    { to: '/voucher-types', icon: FileText, label: 'Voucher Types' },
    { to: '/vouchers', icon: Receipt, label: 'Vouchers' }
  ]

  const settingsNavItems: NavItem[] = [
    { to: '/settings/organization-settings', icon: Building, label: 'Organization Settings' },
    { to: '/settings/units', icon: Ruler, label: 'Units' },
    { to: '/settings/hsn-codes', icon: Hash, label: 'HSN Codes' },
    { to: '/settings/tax-rates', icon: Percent, label: 'Tax Rates' },
    ...(canSeeNotifications
      ? [{ to: '/settings/notifications', icon: Bell, label: 'Notifications' }]
      : [])
  ]

  const accountingNavItems: NavItem[] = [
    { to: '/accounting/chart-of-accounts', icon: ListTree, label: 'Chart of Accounts' },
    { to: '/accounting/account-statement', icon: FileText, label: 'Account Statement' },
    { to: '/day-end-closure', icon: Banknote, label: 'Day-End Cash Closure' },
    { to: '/field-agent-cash', icon: Truck, label: 'Field Agent Cash' },
    { to: '/accounting/razorpay-transactions', icon: CreditCard, label: 'Razorpay Transactions' }
  ]

  const inventoryNavItems: NavItem[] = [
    { to: '/inventory', icon: Warehouse, label: 'Inventory Management', end: true },
    { to: '/inventory/bulk-reconcile', icon: ClipboardList, label: 'Bulk Stock Reconcile' },
    { to: '/settings/categories', icon: FolderTree, label: 'Categories' },
    { to: '/pricing/bulk-update', icon: Tags, label: 'Bulk Price Update' },
    { to: '/pricing/site-availability', icon: MapPin, label: 'Site Availability' }
  ]

  const reportingNavItems: NavItem[] = [
    { to: '/admin/reporting/sales', icon: TrendingUp, label: 'Sales Report' },
    { to: '/admin/reporting/sales-analysis', icon: BarChart3, label: 'Sales Analysis' },
    { to: '/admin/reporting/inventory', icon: Package, label: 'Inventory Report' },
    { to: '/admin/reporting/parties', icon: Users, label: 'Parties Report' },
    { to: '/admin/reporting/delivery', icon: Truck, label: 'Delivery Metrics' },
    { to: '/admin/reporting/customers', icon: Users, label: 'Customer Metrics' },
    { to: '/admin/reporting/winback', icon: UserMinus, label: 'Win-back & Churn' }
  ]

  const navGroups: NavGroup[] = [
    { title: 'Items', icon: Package, items: itemsNavItems },
    { title: 'Quotes', icon: FileClock, items: quotesNavItems },
    { title: 'Sales', icon: ShoppingCart, items: salesNavItems },
    { title: 'Purchases & Expenses', icon: ShoppingBag, items: purchasesNavItems },
    {
      title: 'Admin',
      icon: Shield,
      items: adminTopNavItems,
      subGroups: [
        { title: 'Transactions', icon: Receipt, items: transactionsNavItems },
        { title: 'Accounting', icon: Calculator, items: accountingNavItems },
        { title: 'Inventory', icon: Warehouse, items: inventoryNavItems },
        { title: 'Reporting', icon: FileText, items: reportingNavItems },
        { title: 'Settings', icon: Settings, items: settingsNavItems }
      ]
    }
  ]

  return (
    <Sidebar
      collapsible="icon"
      className={cn(
        'border-r border-sidebar-border [&>[data-sidebar=sidebar]]:bg-gradient-sidebar',
        className
      )}
      {...props}
    >
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              size="lg"
              className="group-data-[collapsible=icon]:!p-0 hover:bg-transparent active:bg-transparent"
            >
              <Link to="/dashboard" onClick={onNavigate}>
                <span className="flex aspect-square size-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary text-[13px] font-bold text-sidebar-primary-foreground shadow-button ring-1 ring-white/20">
                  M
                </span>
                <span className="truncate text-[15px] font-semibold tracking-tight">Mikro</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {navItems.map((item) => (
              <NavLeafLink key={item.to} item={item} onNavigate={onNavigate} />
            ))}
            {navGroups.map((group) => (
              <NavGroupItem key={group.title} group={group} onNavigate={onNavigate} />
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarSeparator className="mx-0" />
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}

export default AppSidebar
